import React, { useState } from 'react';
import { Typography, Button, Space, Input, Select, Tag, Collapse, message, Popconfirm } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Panel } = Collapse;

/**
 * StateManagerPanel - Manages component states (default, hover, active, etc.) with content overrides
 */
const StateManagerPanel = ({
    states = [], // Array of { name: string, overrides: object }
    onChange,
    previewState,
    onPreviewStateChange
}) => {
    const [isAddingState, setIsAddingState] = useState(false);
    const [newStateName, setNewStateName] = useState('');
    const [editingStateIndex, setEditingStateIndex] = useState(null);
    const [editingOverrides, setEditingOverrides] = useState({});

    // Predefined state suggestions
    const predefinedStates = ['default', 'hover', 'active', 'focused', 'disabled', 'loading', 'error', 'success'];

    // Get available states to add (exclude already used ones)
    const availableStates = predefinedStates.filter(
        s => !states.some(state => state.name.toLowerCase() === s.toLowerCase())
    );

    const handleAddState = () => {
        if (!newStateName.trim()) {
            message.warning('State name is required');
            return;
        }
        if (states.some(s => s.name.toLowerCase() === newStateName.toLowerCase())) {
            message.warning('State already exists');
            return;
        }

        const updatedStates = [...states, { name: newStateName.trim(), overrides: {} }];
        onChange?.(updatedStates);
        setNewStateName('');
        setIsAddingState(false);
        message.success(`Added "${newStateName}" state`);
    };

    const handleDeleteState = (index) => {
        if (states[index]?.name === 'default') {
            message.error('Cannot delete default state');
            return;
        }
        const updatedStates = states.filter((_, i) => i !== index);
        onChange?.(updatedStates);
    };

    const handleStartEditOverrides = (index) => {
        setEditingStateIndex(index);
        setEditingOverrides(states[index]?.overrides || {});
    };

    const handleSaveOverrides = () => {
        if (editingStateIndex === null) return;

        const updatedStates = states.map((state, i) =>
            i === editingStateIndex ? { ...state, overrides: editingOverrides } : state
        );
        onChange?.(updatedStates);
        setEditingStateIndex(null);
        setEditingOverrides({});
    };

    const handleCancelEdit = () => {
        setEditingStateIndex(null);
        setEditingOverrides({});
    };

    const stateColors = {
        default: '#58a6ff',
        hover: '#7ee787',
        active: '#ffa657',
        focused: '#a371f7',
        disabled: '#8b949e',
        loading: '#79c0ff',
        error: '#f85149',
        success: '#56d364'
    };

    const getStateColor = (stateName) => {
        return stateColors[stateName.toLowerCase()] || '#8b949e';
    };

    return (
        <div style={{
            padding: 16,
            background: '#0d1117',
            borderRadius: 8,
            border: '1px solid #21262d'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: '1px solid #21262d'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ThunderboltOutlined style={{ color: '#ffa657', fontSize: 14 }} />
                    <Text style={{ color: '#e6edf3', fontWeight: 500 }}>
                        Component States
                    </Text>
                </div>
                <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => setIsAddingState(true)}
                    style={{ color: '#58a6ff' }}
                >
                    Add State
                </Button>
            </div>

            {/* Add State Form */}
            {isAddingState && (
                <div style={{
                    padding: 12,
                    background: '#161b22',
                    borderRadius: 6,
                    marginBottom: 16,
                    border: '1px solid #30363d'
                }}>
                    <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                        NEW STATE
                    </Text>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Select
                            value={newStateName || undefined}
                            onChange={setNewStateName}
                            placeholder="Select or type state name..."
                            style={{ flex: 1 }}
                            showSearch
                            allowClear
                            options={availableStates.map(s => ({
                                value: s,
                                label: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={{
                                            width: 8, height: 8,
                                            borderRadius: '50%',
                                            background: getStateColor(s)
                                        }} />
                                        {s}
                                    </div>
                                )
                            }))}
                        />
                        <Button
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={handleAddState}
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setIsAddingState(false);
                                setNewStateName('');
                            }}
                        />
                    </div>
                </div>
            )}

            {/* State List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {states.map((state, index) => (
                    <div
                        key={state.name}
                        style={{
                            padding: '10px 12px',
                            background: previewState === state.name ? '#161b22' : 'transparent',
                            border: `1px solid ${previewState === state.name ? getStateColor(state.name) : '#30363d'}`,
                            borderRadius: 6,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                        }}
                        onClick={() => onPreviewStateChange?.(state.name)}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                    width: 10, height: 10,
                                    borderRadius: '50%',
                                    background: getStateColor(state.name)
                                }} />
                                <Text style={{
                                    color: '#e6edf3',
                                    fontWeight: state.name === 'default' ? 500 : 400
                                }}>
                                    {state.name}
                                </Text>
                                {state.name === 'default' && (
                                    <Tag style={{
                                        fontSize: 9,
                                        margin: 0,
                                        padding: '0 4px',
                                        background: '#21262d',
                                        border: 'none',
                                        color: '#8b949e'
                                    }}>
                                        base
                                    </Tag>
                                )}
                                {Object.keys(state.overrides || {}).length > 0 && (
                                    <Tag style={{
                                        fontSize: 9,
                                        margin: 0,
                                        padding: '0 4px',
                                        background: getStateColor(state.name) + '30',
                                        border: 'none',
                                        color: getStateColor(state.name)
                                    }}>
                                        {Object.keys(state.overrides).length} override{Object.keys(state.overrides).length > 1 ? 's' : ''}
                                    </Tag>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartEditOverrides(index);
                                    }}
                                    style={{ color: '#8b949e' }}
                                />
                                {state.name !== 'default' && (
                                    <Popconfirm
                                        title="Delete this state?"
                                        onConfirm={() => handleDeleteState(index)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ color: '#8b949e' }}
                                        />
                                    </Popconfirm>
                                )}
                            </div>
                        </div>

                        {/* Overrides Editor (expanded when editing) */}
                        {editingStateIndex === index && (
                            <div
                                style={{
                                    marginTop: 12,
                                    paddingTop: 12,
                                    borderTop: '1px solid #30363d'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                                    CONTENT OVERRIDES
                                </Text>

                                {/* Text Override */}
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ color: '#8b949e', fontSize: 10, display: 'block', marginBottom: 4 }}>
                                        Text Content
                                    </Text>
                                    <Input
                                        value={editingOverrides.text || ''}
                                        onChange={(e) => setEditingOverrides(prev => ({ ...prev, text: e.target.value }))}
                                        placeholder="Override text content..."
                                        size="small"
                                        style={{
                                            background: '#0d1117',
                                            border: '1px solid #30363d'
                                        }}
                                    />
                                </div>

                                {/* Background Color Override */}
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ color: '#8b949e', fontSize: 10, display: 'block', marginBottom: 4 }}>
                                        Background Color
                                    </Text>
                                    <Input
                                        value={editingOverrides.backgroundColor || ''}
                                        onChange={(e) => setEditingOverrides(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                        placeholder="e.g., #ff0000 or primary-500"
                                        size="small"
                                        style={{
                                            background: '#0d1117',
                                            border: '1px solid #30363d'
                                        }}
                                    />
                                </div>

                                {/* Opacity Override */}
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ color: '#8b949e', fontSize: 10, display: 'block', marginBottom: 4 }}>
                                        Opacity
                                    </Text>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={1}
                                        step={0.1}
                                        value={editingOverrides.opacity ?? ''}
                                        onChange={(e) => setEditingOverrides(prev => ({
                                            ...prev,
                                            opacity: e.target.value ? parseFloat(e.target.value) : undefined
                                        }))}
                                        placeholder="e.g., 0.5"
                                        size="small"
                                        style={{
                                            background: '#0d1117',
                                            border: '1px solid #30363d'
                                        }}
                                    />
                                </div>

                                {/* Save/Cancel Buttons */}
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <Button size="small" onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        onClick={handleSaveOverrides}
                                    >
                                        Save Overrides
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {states.length === 0 && (
                <div style={{
                    padding: 24,
                    textAlign: 'center',
                    color: '#8b949e'
                }}>
                    <Text style={{ color: '#484f58' }}>
                        No states defined. Add a "default" state to get started.
                    </Text>
                </div>
            )}
        </div>
    );
};

export default StateManagerPanel;
